/**
 * The one way this app reports a failed request.
 *
 * It always shows the message the API chose (already safe to display — the
 * exception filter replaces anything internal with a generic sentence), and the
 * request id when there is one, so a person can quote it and the line can be
 * found in the server log.
 */
import { RotateCw, TriangleAlert } from 'lucide-react';

import { Button } from '@wend/ui';

import { describeApiError, type ApiError } from '../lib/api-error';

export interface ErrorStateProps {
  error: ApiError;
  title?: string;
  onRetry?: () => void;
  /** Set while the retry is in flight. */
  retrying?: boolean;
}

export function ErrorState({ error, title = 'That did not work', onRetry, retrying = false }: ErrorStateProps) {
  return (
    <div role="alert" className="flex flex-col items-start gap-3 px-6 py-10">
      <span className="flex items-center gap-2 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-bad">
        <TriangleAlert aria-hidden="true" className="size-4" />
        {title}
      </span>

      <p className="max-w-prose text-[0.95rem] leading-relaxed text-muted">
        {describeApiError(error)}
      </p>

      {error.requestId !== undefined && (
        <p className="font-mono text-[0.7rem] tracking-[0.06em] text-muted">
          Request {error.requestId}
        </p>
      )}

      {onRetry && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={retrying}
          leadingIcon={<RotateCw aria-hidden="true" />}
          onClick={onRetry}
        >
          Try again
        </Button>
      )}
    </div>
  );
}
